import React, { useState, useEffect } from 'react';
import { 
  PlusIcon,
  PencilIcon,
  UserGroupIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';

const OrganizationRoleManagement = () => {
  const [roleDefinitions, setRoleDefinitions] = useState([]);
  const [employeesWithRoles, setEmployeesWithRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedView, setSelectedView] = useState('roles'); // roles, assignments, hierarchy
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [assignmentMode, setAssignmentMode] = useState('assign'); // 'assign' or 'edit'
  const [selectedEmployeeForAssignment, setSelectedEmployeeForAssignment] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const [rolesResponse, employeesResponse, deptsResponse] = await Promise.all([
        fetch('/api/centralized-roles/hierarchy', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/centralized-roles/employees-with-roles', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/departments', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Check if responses are ok and contain JSON
      if (!rolesResponse.ok) {
        throw new Error(`Roles API returned ${rolesResponse.status}: ${rolesResponse.statusText}`);
      }
      if (!employeesResponse.ok) {
        throw new Error(`Employees API returned ${employeesResponse.status}: ${employeesResponse.statusText}`);
      }
      if (!deptsResponse.ok) {
        throw new Error(`Departments API returned ${deptsResponse.status}: ${deptsResponse.statusText}`);
      }

      const [roles, employees, departments] = await Promise.all([
        rolesResponse.json(),
        employeesResponse.json(),
        deptsResponse.json()
      ]);

      if (roles.success) {
        setRoleDefinitions(roles.data);
      } else {
        throw new Error(roles.message || 'Failed to load role definitions');
      }
      
      if (employees.success) {
        setEmployeesWithRoles(employees.data);
      } else {
        throw new Error(employees.message || 'Failed to load employees with roles');
      }
      
      // Handle both formats - new API format and legacy format
      if (departments.success) {
        setDepartments(departments.data.departments || departments.data);
      } else if (departments.departments) {
        // Legacy format - direct departments array
        setDepartments(departments.departments);
      } else {
        throw new Error(departments.message || 'Failed to load departments');
      }

    } catch (err) {
      console.error('Error loading role management data:', err);
      setError(err.message || 'Error loading organizational role data');
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = selectedDepartment 
    ? employeesWithRoles.filter(emp => emp.department_id === selectedDepartment)
    : employeesWithRoles;

  const handleDeleteRoleDefinition = async (roleId, roleName) => {
    if (!window.confirm(`Are you sure you want to delete the role definition "${roleName}"? This action cannot be undone and will affect all employees with this role.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/organizational-roles/definitions/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Trigger role synchronization after successful deletion
        try {
          await fetch('/api/centralized-roles/synchronize', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
        } catch (syncError) {
          console.warn('Role sync failed:', syncError.message);
        }
        
        loadData(); // Refresh data
      } else {
        alert(result.message || 'Error deleting role definition');
      }
    } catch (error) {
      alert('Error deleting role definition: ' + error.message);
    }
  };

  const handleRemoveRole = async (employeeId) => {
    if (!window.confirm('Are you sure you want to remove this role assignment?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken');
      // Use a new endpoint to remove role by employee ID and role definition ID
      const employee = employeesWithRoles.find(emp => emp.id === employeeId);
      
      if (!employee || !employee.role_definition_id) {
        alert('No role assignment found for this employee');
        return;
      }
      
      // Use DELETE by employee and role to find the right assignment
      const response = await fetch(`/api/organizational-roles/assignments/employee/${employeeId}/role/${employee.role_definition_id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          removal_reason: 'Removed via role management interface'
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Trigger role synchronization after successful removal
        try {
          await fetch('/api/centralized-roles/synchronize', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
        } catch (syncError) {
          console.warn('Role sync failed:', syncError.message);
        }
        
        loadData(); // Refresh data
      } else {
        alert(result.message || 'Error removing role assignment');
      }
    } catch (error) {
      alert('Error removing role assignment: ' + error.message);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizational Role Management</h1>
          <p className="text-gray-600">Configure organizational roles and manage role assignments</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowRoleModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>Add Role</span>
          </button>
          <button
            onClick={() => setShowAssignmentModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
          >
            <UserGroupIcon className="h-4 w-4" />
            <span>Assign Roles</span>
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
        <button
          onClick={() => setSelectedView('roles')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            selectedView === 'roles'
              ? 'bg-white text-gray-900 shadow'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Role Definitions
        </button>
        <button
          onClick={() => setSelectedView('assignments')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            selectedView === 'assignments'
              ? 'bg-white text-gray-900 shadow'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Role Assignments
        </button>
        <button
          onClick={() => setSelectedView('hierarchy')}
          className={`px-4 py-2 rounded-md text-sm font-medium ${
            selectedView === 'hierarchy'
              ? 'bg-white text-gray-900 shadow'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Hierarchy View
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Content based on selected view */}
      {selectedView === 'roles' && (
        <RoleDefinitionsView 
          roles={roleDefinitions} 
          onEditRole={setEditingRole}
          onDeleteRole={handleDeleteRoleDefinition}
          onRefresh={loadData}
        />
      )}

      {selectedView === 'assignments' && (
        <RoleAssignmentsView 
          employees={filteredEmployees}
          departments={departments}
          selectedDepartment={selectedDepartment}
          onDepartmentChange={setSelectedDepartment}
          onRefresh={loadData}
          onShowAssignmentModal={(employee = null) => {
            if (employee) {
              setAssignmentMode(employee.role_definition_id ? 'edit' : 'assign');
              setSelectedEmployeeForAssignment(employee);
            } else {
              setAssignmentMode('assign');
              setSelectedEmployeeForAssignment(null);
            }
            setShowAssignmentModal(true);
          }}
          onRemoveRole={handleRemoveRole}
        />
      )}

      {selectedView === 'hierarchy' && (
        <OrganizationHierarchyView 
          employees={employeesWithRoles}
          roles={roleDefinitions}
          departments={departments}
        />
      )}

      {/* Role Modal */}
      {(showRoleModal || editingRole) && (
        <RoleModal
          role={editingRole}
          onClose={() => {
            setShowRoleModal(false);
            setEditingRole(null);
          }}
          onSave={loadData}
        />
      )}

      {/* Assignment Modal */}
      {showAssignmentModal && (
        <AssignmentModal
          employees={employeesWithRoles}
          roles={roleDefinitions}
          mode={assignmentMode}
          selectedEmployee={selectedEmployeeForAssignment}
          onClose={() => {
            setShowAssignmentModal(false);
            setSelectedEmployeeForAssignment(null);
            setAssignmentMode('assign');
          }}
          onSave={loadData}
        />
      )}
    </div>
  );
};

const RoleDefinitionsView = ({ roles, onEditRole, onDeleteRole, onRefresh }) => {
  const sortedRoles = [...roles].sort((a, b) => b.hierarchy_level - a.hierarchy_level);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Organizational Role Definitions</h3>
        <p className="text-sm text-gray-500">Configure hierarchy levels and permissions for organizational roles</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hierarchy Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Permissions
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedRoles.map((role) => (
              <tr key={role.role_key} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{role.role_name}</div>
                    <div className="text-sm text-gray-500">{role.role_description}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Level {role.hierarchy_level}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {role.can_approve_workflows && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Workflows
                      </span>
                    )}
                    {role.can_approve_leave && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        Leave
                      </span>
                    )}
                    {role.can_approve_expenses && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        Expenses
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-wrap gap-1">
                    {role.is_management_role && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        Management
                      </span>
                    )}
                    {role.is_executive_role && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        Executive
                      </span>
                    )}
                    {role.is_director_role && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        Director
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEditRole(role)}
                      className="text-indigo-600 hover:text-indigo-900"
                      title="Edit Role"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDeleteRole(role.id, role.role_name)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Role"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const RoleAssignmentsView = ({ employees, departments, selectedDepartment, onDepartmentChange, onRefresh, onShowAssignmentModal, onRemoveRole }) => {
  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Role Assignments</h3>
            <p className="text-sm text-gray-500">View and manage employee role assignments</p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedDepartment}
              onChange={(e) => onDepartmentChange(e.target.value)}
              className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Departments</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Current Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hierarchy Level
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Department
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Effective From
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Effective To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                System Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {employees.map((employee) => (
              <tr key={employee.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-700">
                          {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {employee.first_name} {employee.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {employee.employee_number}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {employee.role_name ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {employee.role_name}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">No organizational role</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  Level {employee.hierarchy_level || 1}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {employee.department || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {employee.effective_from ? new Date(employee.effective_from).toLocaleDateString() : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {employee.effective_to ? new Date(employee.effective_to).toLocaleDateString() : 'Ongoing'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {employee.system_role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => onShowAssignmentModal(employee)}
                      className="text-green-600 hover:text-green-900 text-xs px-2 py-1 border border-green-600 rounded hover:bg-green-50"
                    >
                      {employee.role_definition_id ? 'Edit' : 'Assign'}
                    </button>
                    {employee.role_definition_id && (
                      <button 
                        onClick={() => onRemoveRole(employee.id)}
                        className="text-red-600 hover:text-red-900 text-xs px-2 py-1 border border-red-600 rounded hover:bg-red-50"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OrganizationHierarchyView = ({ employees, roles, departments }) => {
  // Group employees by hierarchy level and role
  const hierarchyGroups = {};
  
  employees.forEach(emp => {
    const level = emp.hierarchy_level || 1;
    if (!hierarchyGroups[level]) {
      hierarchyGroups[level] = [];
    }
    hierarchyGroups[level].push(emp);
  });

  const sortedLevels = Object.keys(hierarchyGroups).sort((a, b) => b - a);

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Organizational Hierarchy</h3>
        <p className="text-sm text-gray-500">Visual representation of role-based hierarchy</p>
      </div>
      
      <div className="p-6 space-y-6">
        {sortedLevels.map(level => (
          <div key={level} className="flex items-start space-x-4">
            <div className="flex-shrink-0 w-20 text-right">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                Level {level}
              </span>
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {hierarchyGroups[level].map(employee => (
                <div key={employee.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-700">
                          {employee.first_name?.[0]}{employee.last_name?.[0]}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {employee.first_name} {employee.last_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {employee.job_title}
                      </p>
                      {employee.role_name && (
                        <p className="text-xs text-green-600">
                          {employee.role_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {employee.can_approve_workflows && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        Approver
                      </span>
                    )}
                    {employee.is_management_role && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        Manager
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Modal components for role management
const RoleModal = ({ role, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    role_key: role?.role_key || '',
    role_name: role?.role_name || '',
    role_description: role?.role_description || '',
    hierarchy_level: role?.hierarchy_level || 1,
    can_approve_workflows: role?.can_approve_workflows || false,
    can_approve_leave: role?.can_approve_leave || false,
    can_approve_expenses: role?.can_approve_expenses || false,
    can_approve_overtime: role?.can_approve_overtime || false,
    approval_limit_amount: role?.approval_limit_amount || '',
    is_management_role: role?.is_management_role || false,
    is_executive_role: role?.is_executive_role || false,
    is_director_role: role?.is_director_role || false,
    sort_order: role?.sort_order || 1
  });
  const [saving, setSaving] = useState(false);
  const [existingRoles, setExistingRoles] = useState([]);
  const [nameError, setNameError] = useState('');
  
  // Load existing roles for validation
  React.useEffect(() => {
    const loadExistingRoles = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch('/api/organizational-roles/definitions', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();
        if (result.success) {
          setExistingRoles(result.data);
        }
      } catch (error) {
        console.error('Error loading existing roles:', error);
      }
    };
    
    loadExistingRoles();
  }, []);
  
  // Validate role name uniqueness
  const validateRoleName = (name) => {
    if (!name.trim()) {
      setNameError('Role name is required');
      return false;
    }
    
    const duplicate = existingRoles.find(existingRole => 
      existingRole.role_name.toLowerCase() === name.toLowerCase() && 
      existingRole.id !== role?.id
    );
    
    if (duplicate) {
      setNameError('Role name already exists. Role names must be unique.');
      return false;
    }
    
    setNameError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate role name before submitting
    if (!validateRoleName(formData.role_name)) {
      return;
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      // Use centralized role system - sync after save
      const url = role 
        ? `/api/organizational-roles/definitions/${role.id}`
        : '/api/organizational-roles/definitions';
      const method = role ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Trigger role synchronization after successful save
        try {
          await fetch('/api/centralized-roles/synchronize', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
        } catch (syncError) {
          console.warn('Role sync failed:', syncError.message);
        }
        
        onSave();
        onClose();
      } else {
        alert(result.message || 'Error saving role');
      }
    } catch (error) {
      alert('Error saving role: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Validate role name on change
    if (name === 'role_name') {
      validateRoleName(newValue);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {role ? 'Edit Role' : 'Create New Role'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role Key *
              </label>
              <input
                type="text"
                name="role_key"
                value={formData.role_key}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., director, manager"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role Name *
              </label>
              <input
                type="text"
                name="role_name"
                value={formData.role_name}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  nameError 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                }`}
                placeholder="e.g., Director, Manager"
              />
              {nameError && (
                <p className="mt-1 text-sm text-red-600">{nameError}</p>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              name="role_description"
              value={formData.role_description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Role description and responsibilities"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hierarchy Level *
              </label>
              <input
                type="number"
                name="hierarchy_level"
                value={formData.hierarchy_level}
                onChange={handleChange}
                min="1"
                max="10"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Order
              </label>
              <input
                type="number"
                name="sort_order"
                value={formData.sort_order}
                onChange={handleChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Approval Limit (Amount)
            </label>
            <input
              type="number"
              name="approval_limit_amount"
              value={formData.approval_limit_amount}
              onChange={handleChange}
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Maximum approval amount"
            />
          </div>
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Permissions</h4>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="can_approve_workflows"
                  checked={formData.can_approve_workflows}
                  onChange={handleChange}
                  className="mr-2"
                />
                Can Approve Workflows
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="can_approve_leave"
                  checked={formData.can_approve_leave}
                  onChange={handleChange}
                  className="mr-2"
                />
                Can Approve Leave
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="can_approve_expenses"
                  checked={formData.can_approve_expenses}
                  onChange={handleChange}
                  className="mr-2"
                />
                Can Approve Expenses
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="can_approve_overtime"
                  checked={formData.can_approve_overtime}
                  onChange={handleChange}
                  className="mr-2"
                />
                Can Approve Overtime
              </label>
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Role Types</h4>
            <div className="grid grid-cols-3 gap-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_management_role"
                  checked={formData.is_management_role}
                  onChange={handleChange}
                  className="mr-2"
                />
                Management Role
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_executive_role"
                  checked={formData.is_executive_role}
                  onChange={handleChange}
                  className="mr-2"
                />
                Executive Role
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_director_role"
                  checked={formData.is_director_role}
                  onChange={handleChange}
                  className="mr-2"
                />
                Director Role
              </label>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || nameError || !formData.role_name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (role ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignmentModal = ({ employees, roles, mode = 'assign', selectedEmployee = null, onClose, onSave }) => {
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(selectedEmployee?.id || '');
  const [selectedRole, setSelectedRole] = useState(selectedEmployee?.role_definition_id || '');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  const [effectiveTo, setEffectiveTo] = useState('');
  const [isPrimaryRole, setIsPrimaryRole] = useState(selectedEmployee?.is_primary_role || false);
  const [assignmentReason, setAssignmentReason] = useState(mode === 'edit' ? 'Role assignment updated' : '');
  const [saving, setSaving] = useState(false);
  
  const isEditMode = mode === 'edit' && selectedEmployee?.role_definition_id;

  // Effect to initialize form when selectedEmployee changes
  React.useEffect(() => {
    if (selectedEmployee) {
      setSelectedEmployeeId(selectedEmployee.id);
      setSelectedRole(selectedEmployee.role_definition_id || '');
      setIsPrimaryRole(selectedEmployee.is_primary_role || false);
      setAssignmentReason(mode === 'edit' ? 'Role assignment updated' : '');
      
      // Set effective dates if editing
      if (mode === 'edit' && selectedEmployee.effective_from) {
        setEffectiveFrom(new Date(selectedEmployee.effective_from).toISOString().split('T')[0]);
      }
      if (mode === 'edit' && selectedEmployee.effective_to) {
        setEffectiveTo(new Date(selectedEmployee.effective_to).toISOString().split('T')[0]);
      } else if (mode === 'edit') {
        setEffectiveTo(''); // Clear if no effective_to date
      }
    }
  }, [selectedEmployee, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployeeId || !selectedRole) {
      alert('Please select both employee and role');
      return;
    }
    
    if (!effectiveFrom) {
      alert('Effective From date is required');
      return;
    }
    
    // For edit mode, skip confirmation as user is already editing
    if (!isEditMode) {
      const selectedEmp = employees.find(emp => emp.id === selectedEmployeeId);
      if (selectedEmp && selectedEmp.role_definition_id) {
        const confirmReassign = window.confirm(
          `${selectedEmp.first_name} ${selectedEmp.last_name} already has the role "${selectedEmp.role_name}". Do you want to reassign them to a new role? This will replace their current organizational role.`
        );
        if (!confirmReassign) {
          return;
        }
      }
    }
    
    setSaving(true);
    
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch('/api/organizational-roles/assign', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          employee_id: selectedEmployeeId,
          role_definition_id: selectedRole,
          effective_from: effectiveFrom,
          effective_to: effectiveTo || null,
          is_primary_role: isPrimaryRole,
          assignment_reason: assignmentReason
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Trigger role synchronization after successful assignment
        try {
          await fetch('/api/centralized-roles/synchronize', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });
        } catch (syncError) {
          console.warn('Role sync failed:', syncError.message);
        }
        
        onSave();
        onClose();
      } else {
        alert(result.message || 'Error assigning role');
      }
    } catch (error) {
      alert('Error assigning role: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {isEditMode ? 'Edit Organizational Role Assignment' : 'Assign Organizational Role'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Employee *
            </label>
            {isEditMode ? (
              <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
                {selectedEmployee.first_name} {selectedEmployee.last_name} ({selectedEmployee.employee_number}) - {selectedEmployee.job_title}
                <input type="hidden" value={selectedEmployeeId} />
              </div>
            ) : (
              <select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose an employee...</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name} ({emp.employee_number}) - {emp.job_title}
                    {emp.role_name ? ` (Current: ${emp.role_name})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Role *
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choose a role...</option>
              {roles.map(role => (
                <option key={role.id} value={role.id}>
                  {role.role_name} (Level {role.hierarchy_level})
                </option>
              ))}
            </select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Effective From *
              </label>
              <input
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Effective To
              </label>
              <input
                type="date"
                value={effectiveTo}
                onChange={(e) => setEffectiveTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isPrimaryRole}
                onChange={(e) => setIsPrimaryRole(e.target.checked)}
                className="mr-2"
              />
              This is the primary role for this employee
            </label>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assignment Reason
            </label>
            <textarea
              value={assignmentReason}
              onChange={(e) => setAssignmentReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Reason for this role assignment"
            />
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? (isEditMode ? 'Updating...' : 'Assigning...') : (isEditMode ? 'Update Role' : 'Assign Role')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationRoleManagement;